import type { Row } from "@tanstack/react-table";

import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type {
	UnprocessedBillingsProjectType,
	UnprocessedInvoicesProjectPhaseType,
	UnprocessedNonProjectInvoice,
	UnprocessedProjectInvoice,
} from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";

export const invoiceTablesDefaultPageState = {
	sorting: [{ id: "deliveryStartDate", desc: true }],
};

export const invoiceTableSharedFilterDefaultPageState: {
	globalFilter: string;
	deliveryPeriodFilter: DateRangeNullable
} = {
	globalFilter: "",
	deliveryPeriodFilter: { startDate: null, endDate: null },
};


export const InvoicePageTabId = {
	Pending: "pending",
	Draft: "draft",
	UNPAID: "unpaid",
	PROCESSED: "processed",
	PROCESSED_WITH_ERRORS: "processedWithErrors",
} as const;

export function createUnprocessedInvoiceTableDataFromProjectInvoice(
	invoiceData: UnprocessedProjectInvoice,
	projectPhases: UnprocessedInvoicesProjectPhaseType[],
	projects: UnprocessedBillingsProjectType[],
): UnprocessedInvoicesIndexTableData {
	const project = projects.find((p) => p.id === invoiceData.projectId);

	const phases = invoiceData.projectPhaseIds.map((phaseId) => projectPhases.find((p) => p.id === phaseId)!);

	if (!project || !phases) {
		throw new Error("Missing project or project phase for invoice");
	}

	const projectNumberParts = project.projectNumber.split("-");
	const projectCounter = parseInt(projectNumberParts[projectNumberParts.length - 1]) || 0;
	let projectNumberWithPhaseNumber = project.projectNumber;

	if (phases.length === 1) {
		projectNumberWithPhaseNumber += " - " + phases[0]?.phaseNumber;
	}

	return {
		allStaffingsClosed: invoiceData.allStaffingsClosed,
		comment: invoiceData.comment,
		companyAvatarImage: project.companyAvatarImage,
		companyDisplayName: project.companyDisplayName || "",
		deliveryEndDate: new Date(invoiceData.deliveryEndDate),
		deliveryStartDate: new Date(invoiceData.deliveryStartDate),
		hasFreelancers: invoiceData.hasFreelancers,
		hasOverbookedOrder: invoiceData.hasOverbookedOrder,
		id: invoiceData.id,
		invoiceNumber:invoiceData.invoiceNumber || null,
		invoiceTypeId: invoiceData.invoiceTypeId,
		invoiceTotalCents: invoiceData.invoiceTotalCents,
		isProjectInvoice: true,
		orderComment: invoiceData.orderComment,
		orderManagedBy: invoiceData.orderManagedBy || null,
		paymentTargetDays: invoiceData.paymentTargetDays || 0,
		orders: invoiceData.orders || [],
		phases: phases,
		projectCounter,
		projectId: invoiceData.projectId,
		projectNumberWithPhaseNumber,
		projectTitle: project.projectTitle,
		quota:invoiceData.quota,
		recipientInformation: null,
		staffingsClosingStatuses: invoiceData.staffingsClosingStatuses,
		timeTrackingsEditable: invoiceData.timeTrackingsEditable,
		vatPercentage: 0,
	};
}

export function createUnprocessedInvoiceTableDataFromNonProjectInvoice(
	invoiceData: UnprocessedNonProjectInvoice,
): UnprocessedInvoicesIndexTableData {

	return {
		allStaffingsClosed: true,
		comment: invoiceData.comment,
		companyAvatarImage: null,
		companyDisplayName: EMPTY_VALUE_PLACEHOLDER,
		deliveryEndDate: new Date(invoiceData.deliveryEndDate),
		deliveryStartDate: new Date(invoiceData.deliveryStartDate),
		hasFreelancers: false,
		hasOverbookedOrder: false,
		id: invoiceData.id,
		invoiceNumber:null,
		invoiceTotalCents: invoiceData.subtotalCents,
		invoiceTypeId:invoiceData.invoiceTypeId,
		isProjectInvoice: false,
		orderComment: null,
		orderManagedBy: null,
		paymentTargetDays: 0,
		orders: [],
		phases: [],
		projectCounter: 0,
		projectId: null,
		projectNumberWithPhaseNumber: EMPTY_VALUE_PLACEHOLDER,
		projectTitle: invoiceData.description,
		quota:null,
		recipientInformation: invoiceData.recipientInformation || null,
		staffingsClosingStatuses: [],
		timeTrackingsEditable: false,
		vatPercentage: invoiceData.vatPercentage,
	};
}

export function projectNumberSortingFunction(
	a: Row<UnprocessedInvoicesIndexTableData>,
	b: Row<UnprocessedInvoicesIndexTableData>,
): number {
	const projectCounterA = a.original.projectCounter;
	const projectCounterB = b.original.projectCounter;
	return projectCounterA - projectCounterB;
}